import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import './OfferCritDetails.scss';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import { MdOpenInFull } from 'react-icons/md';
import Modal from '@srm/ui/modal';
import {
    CritType,
    IOfferAnswerItem,
    IOfferAnswerSetting,
    IOfferCrit,
    ITableDemandItem,
    OfferAnswerType,
} from '@models/winnerSelection/ITableDemand';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import Hint from '@srm/ui/hint';
import { IOfferCritDetailsFormData } from './IOfferCritDetailsFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import {
    RatingScaleDefault,
    RatingScalePirSmr,
    RatingScalePSTZ,
    RatingScaleSimTum,
    RatingScaleTMC,
} from './RatingScales';
import { Divider } from '@mantine/core';
import CheckboxControl from '@srm/ui/controls/CheckboxControl';
import { IOption } from '@srm/ui/types/IOption';
import DateFieldControl from '@srm/ui/controls/DateFieldControl';
import DateTimeFieldControl from '@srm/ui/controls/DateTimeFieldControl';
import InputNumberControl from '@srm/ui/controls/InputNumberControl';
import SelectControl from '@srm/ui/controls/SelectControl';
import { loadMathJs } from '@utils/mathjsLoader';
import { compileScriptFunction } from '@utils/scriptEval';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { getOfferAnswerSetting, normalizeOfferAnswerType } from '../utils/answerTypes';

export interface IOfferCritDetailsProps {
    tableKey: string;
    isEdit: boolean;
    demandId: string;
    offerId: string;
    disabledHint?: string;
    ratingScaleHints?: Record<string, string> | null;
    customScaleCrits?: string[] | null;
    editButtonName?: string | null;
    allowGroupActions?: boolean;
    /** false — только отметка «на все предложения поставщика», без лота/позиции. */
    allowLotGroupAction?: boolean;
    supplierGroupLabel?: string;
    supplierGroupDescription?: string;
    value: IOfferCrit;
    rowData: ITableDemandItem;
    onSubmit?: (value: IOfferCritDetailsFormData) => void;
    saveAction?: (value: IOfferCritDetailsFormData) => Promise<void>;
    offerAnswerSettings?: IOfferAnswerSetting[] | null;
    showAnswerInScore?: boolean;
    showComment?: boolean;
    contextHint?: string;
    preserveDraftOnUpdate?: boolean;
}

function evalPredicateWithAnswer(
    requiredPredicate: string | null | undefined,
    answer: string | null | undefined,
): boolean {
    if (!requiredPredicate?.trim()) return false;

    // Подставляем {answer} => "строка" c экранированием
    const expr = requiredPredicate.replace(/\{answer\}/g, JSON.stringify(answer ?? ''));

    try {
        const result = compileScriptFunction([], `"use strict"; return (${expr});`)();
        return !!result;
    } catch {
        return false;
    }
}

const checkCommentRequired = (
    score: number | null,
    critType: Exclude<CritType, null>,
    critName: string,
    answers: IOfferAnswerItem[],
    customScaleCrits?: string[] | null,
    requiredPredicate?: string | null,
    answer?: string | null,
) => {
    if (critType === 'info') return false;

    if (critType === 'answer') {
        // считаем по предикату (через eval); если предиката нет - по атрибуту выбранного ответа, если нет - не требуем комментарий
        return requiredPredicate
            ? evalPredicateWithAnswer(requiredPredicate, answer)
            : (answers.find((a) => a.value === answer)?.commentRequired ?? false);
    }

    if (!customScaleCrits?.includes(critName)) return false;
    if (score === null) return false;
    return score < 3.0;
};

const getEffectiveCritType = (row: ITableDemandItem): Exclude<CritType, null> =>
    row.critType ?? (row.isInfoCrit ? 'info' : 'score');

const isInList = (val: string | null | undefined, list?: IOfferAnswerItem[]) =>
    !!val && !!list?.some((a) => a.value === val);

const getInitialAnswerSelect = (
    answer: string | null | undefined,
    currentSelect: string | null | undefined,
    answerSetting: IOfferAnswerSetting | null,
): string => {
    if (!answerSetting) return currentSelect ?? '';
    if (isInList(answer, answerSetting.answers)) return answer ?? '';
    if (answerSetting.allowCustomAnswer && answer) return '__custom__';
    return currentSelect ?? '';
};

const yesNoOptions: IOption[] = [
    { value: 'Да', label: 'Да' },
    { value: 'Нет', label: 'Нет' },
];

const getAnswerPlaceholder = (type: OfferAnswerType, allowCustomAnswer?: boolean): string | null => {
    switch (type) {
        case 'select':
            return allowCustomAnswer ? 'Выберите вариант или укажите свой ответ' : 'Выберите вариант';
        case 'date':
            return 'ДД.ММ.ГГГГ';
        case 'datetime':
            return 'ДД.ММ.ГГГГ ЧЧ:ММ';
        case 'money':
            return 'Введите сумму числом';
        case 'integer':
            return 'Введите целое число';
        case 'yesno':
            return 'Выберите Да или Нет';
        default:
            return null;
    }
};

/** Императивный хэндл: позволяет родительской ячейке открыть модалку при клике
 *  по всей её площади (а не только по pill «Подробнее»). */
export interface IOfferCritDetailsHandle {
    open: () => void;
}

const OfferCritDetails = forwardRef<IOfferCritDetailsHandle, IOfferCritDetailsProps>(
    (
        {
            tableKey,
            isEdit,
            disabledHint,
            ratingScaleHints,
            customScaleCrits,
            editButtonName,
            allowGroupActions = false,
            allowLotGroupAction = true,
            supplierGroupLabel = 'На все предложения поставщика',
            supplierGroupDescription = 'Введенные сведения по критерию будут также перенесены на все предложения поставщика',
            demandId,
            offerId,
            value,
            rowData,
            onSubmit,
            saveAction,
            offerAnswerSettings,
            showAnswerInScore = false,
            showComment = true,
            contextHint,
            preserveDraftOnUpdate = false,
        },
        ref,
    ) => {
        const [showModal, setShowModal] = useState<boolean>(false);
        const [error, setError] = useState<string>();

        useImperativeHandle(ref, () => ({ open: () => setShowModal(true) }), []);

        const critType = getEffectiveCritType(rowData);
        const showScoreAnswer = critType === 'score' && showAnswerInScore;
        const critInfo = (rowData.critInfo ?? []).filter((item) => item.value?.trim());

        const methods = useForm<IOfferCritDetailsFormData>({ mode: 'onBlur' });
        const { isSubmitting, isDirty } = methods.formState;
        // Escape и клик по фону закрывают окно: изменённую оценку без вопроса не теряем
        const hasUnsavedInput = useUnsavedFormInput(methods);

        // авторасчёт рейтинга
        const watchCalcValues = useWatch({ name: ['offerCrit.score', 'weight'], control: methods.control });

        // текущее значение ответа
        const currentAnswer = useWatch({ name: 'offerCrit.answer', control: methods.control });
        const answerSelect = useWatch({ name: 'offerCrit.answerSelect', control: methods.control });

        // Настройка может быть привязана к типу ответа или к конкретному критерию.
        const answerSetting = useMemo(() => {
            if (critType !== 'answer') return null;
            return getOfferAnswerSetting(rowData, offerAnswerSettings);
        }, [critType, rowData.answerType, rowData.critId, offerAnswerSettings]);
        const answerControlType = answerSetting?.type ?? normalizeOfferAnswerType(rowData.answerType) ?? 'text';
        const answerPlaceholder = getAnswerPlaceholder(answerControlType, answerSetting?.allowCustomAnswer);

        // опции для SelectControl (answer)
        const answerOptions: IOption[] | undefined = useMemo(() => {
            if (answerControlType !== 'select') return undefined;
            if (!answerSetting) return undefined;
            const base = (answerSetting.answers ?? []).map<IOption>((a) => ({ value: a.value, label: a.value }));
            return answerSetting.allowCustomAnswer ? [...base, { value: '__custom__', label: 'Другое…' }] : base;
        }, [answerControlType, answerSetting]);

        // текущее значение селекта (конверсия из строки ответа)
        const selectValueOptions: IOption[] | undefined = useMemo(() => {
            if (!answerOptions) return undefined;
            if (!answerSetting) return undefined;

            // если ответ из списка - выбираем его; если нет и разрешён кастом - выбираем "Другое…"
            const inList = isInList(currentAnswer, answerSetting.answers);
            if (inList) {
                const found = answerOptions.find((o) => o.value === currentAnswer);
                return found ? [found] : [];
            }
            if (answerSetting.allowCustomAnswer) {
                const customOpt = answerOptions.find((o) => o.value === '__custom__');
                return customOpt ? [customOpt] : [];
            }
            return [];
        }, [answerOptions, answerSetting, currentAnswer]);

        // answerSelect инициализируется в reset() ниже через getInitialAnswerSelect —
        // отдельный init-эффект убран как дублирующий источник истины.

        // синхронизация: при выборе предустановленной опции копируем её в offerCrit.answer
        useEffect(() => {
            if (critType !== 'answer' || answerControlType !== 'select' || !answerSetting) return;
            const sel = methods.getValues('offerCrit.answerSelect') as string | undefined;
            if (!sel) return;

            if (sel !== '__custom__') {
                // выбран вариант из списка - пишем его в текст ответа
                methods.setValue('offerCrit.answer', sel, { shouldDirty: true });
            } else {
                // выбран "Другое…" - не трогаем текст; пользователь редактирует offerCrit.answer вручную
            }
        }, [showModal, answerSelect, critType, answerControlType, answerSetting, methods]);

        // требование комментария
        const [commentRequired, setCommentRequired] = useState(() =>
            checkCommentRequired(
                value?.score ?? null,
                critType,
                rowData.critName,
                answerSetting?.answers ?? [],
                customScaleCrits,
                rowData.commentRequiredPredicate,
                value?.answer ?? null,
            ),
        );

        useEffect(() => {
            const values = methods.getValues();
            if (values.offerCrit && values.offerCrit.score !== null && values.weight !== null) {
                const score = values.offerCrit.score;
                const weight = values.weight;
                loadMathJs().then(({ round }) => {
                    const newRating = round(score * weight, 4);
                    methods.setValue('offerCrit.rating', newRating, { shouldDirty: true });
                });
            }

            const isCommentReq = checkCommentRequired(
                values?.offerCrit?.score ?? null,
                critType,
                rowData.critName,
                answerSetting?.answers ?? [],
                customScaleCrits,
                rowData.commentRequiredPredicate,
                currentAnswer ?? null,
            );

            setCommentRequired(isCommentReq);
        }, [
            watchCalcValues,
            currentAnswer,
            critType,
            rowData.commentRequiredPredicate,
            rowData.ratingScale,
            rowData.critName,
            customScaleCrits,
            answerSetting?.answers,
        ]);

        const initializedForOpen = useRef(false);
        useEffect(() => {
            // A conflict refreshes the matrix, but the open editor must retain the user's draft.
            if (preserveDraftOnUpdate) {
                if (!showModal) initializedForOpen.current = false;
                else if (initializedForOpen.current) return;
                else initializedForOpen.current = true;
            }
            const defaultValue: IOfferCritDetailsFormData = {
                offerCrit: {
                    ...value,
                    answerSelect:
                        critType === 'answer' && answerControlType === 'select'
                            ? getInitialAnswerSelect(value?.answer, value?.answerSelect, answerSetting)
                            : value?.answerSelect,
                },
                critId: rowData.critId,
                weight: rowData.critWeight,
            };
            methods.reset(defaultValue);
        }, [
            value,
            showModal,
            critType,
            answerControlType,
            answerSetting,
            methods,
            rowData.critId,
            rowData.critWeight,
            preserveDraftOnUpdate,
        ]);

        const onClick = () => setShowModal(!showModal);

        const onFormSubmit = async (form: IOfferCritDetailsFormData) => {
            setError(undefined);
            const saving = saveAction
                ? saveAction(form)
                : WinnerSelectionService.saveTableDemandOfferCrit(offerId, demandId, tableKey, form);
            return saving
                .then(() => {
                    sendNotification({ message: 'Данные успешно сохранены', variant: 'green' });
                    setShowModal(!showModal);
                    onSubmit && onSubmit(form);
                })
                .catch((reason) => {
                    setError(reason instanceof Error ? reason.message : String(reason));
                });
        };

        const errorLabel = error && (
            <div className="offer-crit-details__error">
                <Hint role="alert" title={error} tone="danger" />
            </div>
        );

        const modalFooter = (
            <div className={'modal-dlg-buttons'}>
                <Button
                    type="submit"
                    form="offer-crit-details-form"
                    loading={isSubmitting}
                    className={clsx('offer-crit-details__button', 'offer-crit-details__save-button')}
                    disabled={isSubmitting || !isDirty}
                >
                    Сохранить
                </Button>
            </div>
        );

        const renderRatingScale = () => {
            if (!customScaleCrits?.includes(rowData.critName))
                return <RatingScaleDefault className="offer-crit-details__column" isEdit={isEdit} methods={methods} />;

            const hint = ratingScaleHints?.[rowData.ratingScale];

            switch (rowData.ratingScale) {
                case 'simtum':
                    return (
                        <RatingScaleSimTum
                            className="offer-crit-details__column"
                            isEdit={isEdit}
                            methods={methods}
                            hint={hint}
                        />
                    );
                case 'pirsmr':
                    return (
                        <RatingScalePirSmr
                            className="offer-crit-details__column"
                            isEdit={isEdit}
                            methods={methods}
                            hint={hint}
                        />
                    );
                case 'pstz':
                    return (
                        <RatingScalePSTZ
                            className="offer-crit-details__column"
                            isEdit={isEdit}
                            methods={methods}
                            hint={hint}
                        />
                    );
                case 'tmcws':
                    return (
                        <RatingScaleTMC
                            className="offer-crit-details__column"
                            isEdit={isEdit}
                            methods={methods}
                            hint={hint}
                        />
                    );
                default:
                    return (
                        <RatingScaleDefault className="offer-crit-details__column" isEdit={isEdit} methods={methods} />
                    );
            }
        };

        const renderAnswerControl = () => {
            const disabled = !isEdit || rowData.answerReadonly;
            const commonProps = {
                label: 'Ответ',
                name: 'offerCrit.answer',
                readOnly: disabled,
                disabled,
                placeholder: answerPlaceholder ?? undefined,
                control: methods.control,
            };

            if (answerControlType === 'select' && answerSetting && answerOptions) {
                return (
                    <>
                        <SelectControl
                            label="Ответ"
                            name="offerCrit.answerSelect"
                            readOnly={true}
                            required={false}
                            disabled={disabled}
                            placeholder={answerPlaceholder ?? undefined}
                            highlightMenuItems={false}
                            options={answerOptions}
                            control={methods.control}
                            setValue={(opts: IOption[]) => {
                                methods.setValue('offerCrit.answer', '', { shouldDirty: true });
                                return opts?.[0]?.value ?? '';
                            }}
                            getValue={(opts: IOption[], value: any) => {
                                if (!opts) return [];
                                const match = opts.find((o) => o.value === value);
                                return match ? [match] : [];
                            }}
                        />

                        {answerSetting.allowCustomAnswer && selectValueOptions?.[0]?.value === '__custom__' && (
                            <TextareaControl
                                {...commonProps}
                                label="Свой ответ"
                                placeholder="Укажите свой ответ"
                                autoResize={true}
                            />
                        )}
                    </>
                );
            }

            if (answerControlType === 'yesno') {
                return (
                    <SelectControl
                        label="Ответ"
                        name="offerCrit.answer"
                        readOnly={true}
                        required={false}
                        disabled={disabled}
                        placeholder={answerPlaceholder ?? undefined}
                        highlightMenuItems={false}
                        options={yesNoOptions}
                        control={methods.control}
                        setValue={(opts: IOption[]) => opts?.[0]?.value ?? ''}
                        getValue={(opts: IOption[], value: any) => {
                            if (!opts) return [];
                            const match = opts.find((o) => o.value === value);
                            return match ? [match] : [];
                        }}
                    />
                );
            }

            if (answerControlType === 'date') {
                return <DateFieldControl {...commonProps} isValueAsString={true} />;
            }

            if (answerControlType === 'datetime') {
                return <DateTimeFieldControl {...commonProps} isValueAsString={true} />;
            }

            if (answerControlType === 'money') {
                return <InputNumberControl {...commonProps} floatPoints={2} groupBy={3} isValueAsString={true} />;
            }

            if (answerControlType === 'integer') {
                return <InputNumberControl {...commonProps} floatPoints={0} groupBy={3} isValueAsString={true} />;
            }

            return <TextareaControl {...commonProps} autoResize={true} />;
        };

        return (
            <>
                {/* pill — визуальный аффорданс + клик мышью. Клик ловит и сама ячейка
                (.ws-cell, role=button), и кнопка ниже, поэтому onClick на этой
                обёртке избыточен. Клавиатурный контрол — ячейка (pill вне tab-order). */}
                <div className={clsx('offer-crit-details')}>
                    <Button
                        type="button"
                        aria-label="Показать детали"
                        variant={'secondary'}
                        tabIndex={-1}
                        startAdornment={<MdOpenInFull size="12px" aria-hidden />}
                        onClick={onClick}
                    >
                        {editButtonName ?? 'Показать детали'}
                    </Button>
                </div>

                {showModal ? (
                    <Modal
                        size="xl"
                        onClose={() => setShowModal(false)}
                        hasUnsavedInput={() => isEdit && hasUnsavedInput()}
                        closeDisabled={isSubmitting}
                        header={rowData.critName}
                        footer={isEdit ? modalFooter : null}
                    >
                        {errorLabel}
                        {contextHint && <p className="offer-crit-details__hint">{contextHint}</p>}

                        {critInfo.length > 0 ? (
                            <div className="offer-crit-details__crit-info">
                                {critInfo.map((item, index) => (
                                    <div className="offer-crit-details__crit-info-item" key={item.key || index}>
                                        {item.title ? (
                                            <div className="offer-crit-details__crit-info-title">{item.title}</div>
                                        ) : null}
                                        <div className="offer-crit-details__crit-info-value">{item.value}</div>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {!isEdit && disabledHint && (
                            <div className={'offer-crit-details__hint'}>
                                <Hint title={disabledHint} tone="warning" maxWidth="100%" />
                            </div>
                        )}

                        <FormProviderControl
                            formMethods={methods}
                            id="offer-crit-details-form"
                            className="offer-crit-details__form"
                            onSubmit={onFormSubmit}
                        >
                            <HookFormProvider controller={Controller}>
                                <div className="offer-crit-details__content">
                                    {critType === 'score' && (
                                        <div className="offer-crit-details__row">{renderRatingScale()}</div>
                                    )}

                                    {showScoreAnswer && (
                                        <div className="offer-crit-details__row">
                                            <TextareaControl
                                                label="Ответ"
                                                name="offerCrit.answer"
                                                readOnly={!isEdit || rowData.answerReadonly}
                                                disabled={!isEdit || rowData.answerReadonly}
                                                autoResize={true}
                                                control={methods.control}
                                            />
                                        </div>
                                    )}

                                    {critType === 'answer' && (
                                        <div className="offer-crit-details__row">{renderAnswerControl()}</div>
                                    )}

                                    {showComment && (
                                        <div className="offer-crit-details__row">
                                            <TextareaControl
                                                label={critType === 'score' ? 'Обоснование' : 'Комментарий'}
                                                name="offerCrit.description"
                                                readOnly={!isEdit}
                                                disabled={!isEdit}
                                                required={commentRequired}
                                                autoResize={true}
                                                rules={{
                                                    required: { value: commentRequired, message: REQUIRED_MESSAGES.text },
                                                }}
                                                control={methods.control}
                                            />
                                        </div>
                                    )}

                                    {allowGroupActions && isEdit && (
                                        <div
                                            className={clsx(
                                                'offer-crit-details__row',
                                                'offer-crit-details__group-actions-row',
                                            )}
                                        >
                                            <Divider color="var(--neutral-20)" />
                                            <CheckboxControl
                                                label={supplierGroupLabel}
                                                description={supplierGroupDescription}
                                                name="offerCrit.applyToAllSupplierOffers"
                                                control={methods.control}
                                                className="offer-crit-details__allSupplierOffers"
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        methods.setValue('offerCrit.applyToAllLotOffers', false, {
                                                            shouldDirty: false,
                                                        });
                                                    }
                                                }}
                                            />
                                            {allowLotGroupAction && (
                                                <CheckboxControl
                                                    label="На все предложения по лоту/позиции"
                                                    description="Введенные сведения по критерию будут также перенесены на все предложения, поданные на лот/позицию"
                                                    name="offerCrit.applyToAllLotOffers"
                                                    control={methods.control}
                                                    className="offer-crit-details__applyToAllLotOffers"
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            methods.setValue(
                                                                'offerCrit.applyToAllSupplierOffers',
                                                                false,
                                                                {
                                                                    shouldDirty: false,
                                                                },
                                                            );
                                                        }
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </HookFormProvider>
                        </FormProviderControl>
                    </Modal>
                ) : null}
            </>
        );
    },
);

OfferCritDetails.displayName = 'OfferCritDetails';

export default OfferCritDetails;
